import { FC, useRef, useState } from 'react';
import { Box, MenuItem, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { KeyboardArrowDownRounded as KeyboardArrowDownRoundedIcon } from '@mui/icons-material';
// interfaces
import { SelectControllerProps } from 'interfaces';
// styles
import { flexCenter } from 'theme/styleConstant';

const SelectController: FC<SelectControllerProps> = ({
  name,
  title,
  options,
  disabled,
  isRequired,
  defaultValue,
  handleChange,
  margin = 'dense',
  endAdornment,
}) => {
  const { control } = useFormContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    if (inputRef.current) {
      // 1. Find the MUI Select trigger div inside the TextField
      // MUI renders a div with the class "MuiSelect-select" as the click target
      const selectTrigger = inputRef.current.querySelector<HTMLElement>('[role="combobox"], [role="button"]');

      if (selectTrigger) {
        // 2. MUI listens for 'mousedown' to open the menu
        selectTrigger.dispatchEvent(
          new MouseEvent('mousedown', {
            bubbles: true,
            cancelable: true,
            view: window,
          }),
        );
      } else {
        console.error('Could not find the select trigger element inside the container.');
      }
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { name, onBlur, onChange, value } = field;
        const { error } = fieldState;
        const { message } = error || {};
        return (
          <TextField
            select
            ref={inputRef}
            fullWidth
            id={name}
            name={name}
            value={value}
            margin={margin}
            onBlur={onBlur}
            variant="outlined"
            disabled={disabled}
            onChange={(e) => {
              onChange(e);
              handleChange && handleChange(value as string);
            }}
            helperText={message}
            error={Boolean(error)}
            InputProps={{
              ...(endAdornment && {
                endAdornment: (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                    <Box
                      sx={{
                        ...flexCenter,
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease-in-out',
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                      onClick={handleOpen}>
                      <KeyboardArrowDownRoundedIcon />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>{endAdornment}</Box>
                  </Box>
                ),
              }),
            }}
            SelectProps={{
              open,
              onOpen: () => setOpen(true),
              onClose: () => setOpen(false),
              IconComponent: endAdornment
                ? () => null
                : (props) => (
                    <Box sx={flexCenter} color="customGrayColor.light" {...props}>
                      <KeyboardArrowDownRoundedIcon />
                    </Box>
                  ),
            }}
            defaultValue={defaultValue}
            label={isRequired ? `${title} *` : title}>
            {options?.map((item) => {
              const { name, value } = item;
              return (
                <MenuItem id={name} key={value} value={value}>
                  {name}
                </MenuItem>
              );
            })}
          </TextField>
        );
      }}
    />
  );
};

export default SelectController;
